/* ==========================================================================
   X-HEALTHY — overlay.js
   <x-drawer> and <x-modal>, both wrapping a native <dialog> (focus trap,
   Esc to close and top-layer for free). Open with any element carrying
   data-overlay-open="<id>", close with data-overlay-close inside.
   ========================================================================== */

(() => {
  const { theme } = window;

  class XOverlay extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || this.dataset.ready) return;
      this.dataset.ready = 'true';

      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });

      // Click on the backdrop (the dialog box itself, outside the panel)
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
        if (event.target.closest('[data-overlay-close]')) this.close();
      });
    }

    get isOpen() {
      return this.dialog?.open;
    }

    open(trigger) {
      if (!this.dialog || this.isOpen) return;
      this.trigger = trigger || document.activeElement;
      this.dialog.classList.remove('is-closing');
      this.dialog.showModal();
      theme.utils.lockScroll();
      this.trigger?.setAttribute?.('aria-expanded', 'true');
      const autofocus = this.dialog.querySelector('[autofocus]') || this.dialog.querySelector('[data-overlay-close]');
      autofocus?.focus({ preventScroll: true });
      this.dispatchEvent(new CustomEvent('overlay:open', { bubbles: true }));
    }

    close() {
      if (!this.isOpen || this.dialog.classList.contains('is-closing')) return;
      const finish = () => {
        this.dialog.classList.remove('is-closing');
        this.dialog.close();
        theme.utils.unlockScroll();
        this.trigger?.setAttribute?.('aria-expanded', 'false');
        if (this.trigger?.isConnected) this.trigger.focus({ preventScroll: true });
        this.dispatchEvent(new CustomEvent('overlay:close', { bubbles: true }));
      };
      if (theme.utils.prefersReducedMotion()) return finish();
      this.dialog.classList.add('is-closing');
      const panel = this.dialog.querySelector('.overlay__panel') || this.dialog;
      let done = false;
      const end = () => {
        if (done) return;
        done = true;
        finish();
      };
      panel.addEventListener('animationend', end, { once: true });
      setTimeout(end, 400);
    }

    toggle(trigger) {
      this.isOpen ? this.close() : this.open(trigger);
    }
  }

  if (!customElements.get('x-drawer')) customElements.define('x-drawer', class XDrawer extends XOverlay {});
  if (!customElements.get('x-modal')) customElements.define('x-modal', class XModal extends XOverlay {});

  // Global triggers
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-overlay-open]');
    if (!trigger) return;
    const overlay = document.getElementById(trigger.dataset.overlayOpen);
    if (!overlay || typeof overlay.open !== 'function') return;
    event.preventDefault();
    overlay.open(trigger);
  });

  theme.overlays = {
    open: (id, trigger) => document.getElementById(id)?.open(trigger),
    close: (id) => document.getElementById(id)?.close(),
  };
})();
